import React, { useEffect, useState, Suspense } from 'react';

import _ from 'lodash';
import cx from 'classnames';

import { DefaultAssetViewer } from './defaultViewer';
import { getContentForFieldSelector } from './fields';
import Content from '../widgets/core/content';

import { EmbedContext, WidgetsContext } from "./../widgets/core/context";


import styles from './assetviewer.module.scss';

const DocumentContentViewer = React.lazy(
  () => import('./documentViewer').then(module => ({ default: module.DocumentContentViewer }))
);

const HTMLViewer = React.lazy(
  () => import('@paperflite-marketing/asset-viewer').then(module => ({ default: module.HTMLViewer }))
);

const ScormViewer = React.lazy(
  () => import('@paperflite-marketing/asset-viewer').then(module => ({ default: module.ScormViewer }))
);

const ImageViewer = React.lazy(
  () => import('@paperflite-marketing/asset-viewer').then(module => ({ default: module.ImageViewer }))
);

const VideoViewer = React.lazy(
  () => import('@paperflite-marketing/asset-viewer').then(module => ({ default: module.VideoViewer }))
);

const AudioViewer = React.lazy(
  () => import('@paperflite-marketing/asset-viewer').then(module => ({ default: module.AudioViewer }))
);

export const AssetContentViewer = ({ 
  asset, isDarkMode=false, lightBox=false, active, onLoad, loaded, onAnalyticsChange, onDebugAnalytics, 
  scrollParent, inline, widgetId, poster 
}) => {

  const id = _.get(asset, 'content.asset.id');
  const type = _.get(asset, 'content.asset.viewer.type');
  const [width, setWidth] = useState(1200);
  const [render, setRender] = React.useState(!inline);

  const context = React.useContext(WidgetsContext);
  const embedContext = React.useContext(EmbedContext);

  const storyboard = _.get(context, 'storyboard');

  React.useEffect(() => {
    window.addEventListener('resize', handleWidowResize);
    calculatePreferredWidth();
    handleViewerLoaded();

    if(embedContext.embedded) {
      const script = _.get(storyboard, 'customScript', null);
      if(script !== null) {
        injectCustomScript(script);
      }
    }

    return () => {
      window.removeEventListener('resize', handleWidowResize);
    };
  }, []);

  React.useEffect(() => {
    if (!render) {
      setRender(true);
    }
  }, [])

  const handleWidowResize = () => {
    calculatePreferredWidth();
  }

  const calculatePreferredWidth = () => {
    const viewerContainer = document.querySelector(`[data-asset="${_.get(asset, 'uid', null)}"]`);
    if (viewerContainer) {
      const width = viewerContainer.offsetWidth;
      setWidth(width);
    }
  }

  const handleViewerLoaded = () => {
    if (!_.includes(['enhanced_viewer', 'pdf_viewer'], type)) {
      onLoad();
    }
  }


  const injectCustomScript = (scriptText) => {
    var script = document.createElement("script")

    if(scriptText) {
      script.appendChild(document.createTextNode(scriptText));
    }

    try {
      document.head.appendChild(script);
    } catch (e) {
      script.text = scriptText;
      document.head.appendChild(script);
    }
  }

  if (!render) {
    return null;
  }

  switch (type) {
    case 'enhanced_viewer':
    case 'pdf_viewer': {
      return (
        <DocumentContentViewer
          asset={asset}
          isDarkMode={isDarkMode}
          lightBox={lightBox}
          width={width}
          active={active}
          widgetId={widgetId}
          inline={inline}
          onLoad={onLoad}
          loaded={loaded}
          scrollParent={scrollParent}
          onAnalyticsChange={onAnalyticsChange}
          onDebugAnalytics={onDebugAnalytics}
        />
      );
    }

    case 'html_viewer': {
      const id = _.get(asset, 'content.asset.id');
      const mode = _.get(asset, 'content.asset.viewer.mode');
      const url = _.get(asset, 'content.asset.metadata.url');
      const optimizedUrl = _.get(asset, 'content.asset.viewer.url');
      const canPreview = _.get(asset, 'content.asset.viewer.preview');

      const name = getContentForFieldSelector(asset, 'name') || "";
      const icon = getContentForFieldSelector(asset, 'icon');
      const iconUrl = _.get(icon, 'url') || '/public/cleverstory/v2/static/default_card.jpg';

      return (
        <Suspense fallback={<div className={styles.htmlViewer}></div>}>
          <HTMLViewer
            id={widgetId ? widgetId : id}
            className={styles.htmlViewer}
            name={name}
            icon={iconUrl}
            mode={mode}
            url={url}
            optimizedUrl={optimizedUrl}
            canPreview={canPreview}
            active={active}
            isDarkMode={isDarkMode}
            lightBox={lightBox}
            onAnalyticsChange={onAnalyticsChange}
            onDebugAnalytics={onDebugAnalytics}
          />
        </Suspense>
      );
    }

    case 'scorm_viewer': {
      const id = _.get(asset, 'content.asset.id');
      const url = _.get(asset, 'content.asset.viewer.url');

      const name = getContentForFieldSelector(asset, 'name') || "";
      const icon = getContentForFieldSelector(asset, 'icon');
      const iconUrl = _.get(icon, 'url') || '/public/cleverstory/v2/static/default_card.jpg';

      return (
        <Suspense fallback={<div className={styles.htmlViewer}></div>}>
          <ScormViewer
            id={widgetId ? widgetId : id}
            className={styles.htmlViewer}
            name={name}
            icon={iconUrl}
            url={url}
            active={active}
            isDarkMode={isDarkMode}
            lightBox={lightBox}
            onAnalyticsChange={onAnalyticsChange}
            onDebugAnalytics={onDebugAnalytics}
          />
        </Suspense>
      );
    }

    case 'image_viewer': {
      const id = _.get(asset, 'content.asset.id');
      const sourceUrl = _.get(asset, 'content.asset.viewer.url');

      return (
        <Suspense fallback={<div className={styles.imageViewer}></div>}>
          <ImageViewer
            id={`asset_viewer_${widgetId ? widgetId : id}`}
            className={styles.imageViewer}
            src={sourceUrl}
            scrollParent={`.${styles.assetViewerContent}`}
            loadingComponent={() => {
              return (
                <div className={cx(styles.loader, {[isDarkMode? styles.darkLoader : styles.lightLoader]: lightBox})}>
                  <div className={styles.videoWrapper}>
                    {!lightBox && <video loop autoPlay playsInline muted>
                      <source src="/images/Workspaceloader.webm" type="video/webm" />
                      <source src="/images/Workspaceloader.mp4" type="video/mp4" />
                    </video>}
                    <label className={cx(styles.message, {[isDarkMode? styles.darkMessage : styles.lightMessage]: lightBox})}>
                      Loading Image....
                    </label>
                  </div>
                </div>
              );
            }}
            active={active}
            isDarkMode={isDarkMode}
            lightBox={lightBox}
            onAnalyticsChange={onAnalyticsChange}
            onDebugAnalytics={onDebugAnalytics}
          />
        </Suspense>
      );
    }

    case 'video_viewer': {
      const id = _.get(asset, 'content.asset.id');
      const metadata = _.get(asset, 'content.asset.metadata');
      const sourceUrl = _.get(asset, 'content.asset.viewer.url');
      const sources = _.get(asset, 'content.asset.viewer.sources');
      const videoProvider = _.get(metadata, 'external_video_provider');

      const icon = getContentForFieldSelector(asset, 'icon');
      const iconUrl = _.get(icon, 'url') || '/public/cleverstory/v2/static/default_card.jpg';

      if (videoProvider == null || videoProvider == 'youtube' || videoProvider == 'vimeo' || videoProvider == 'wistia') {
        return (
          <Suspense fallback={<div className={cx(styles.videoViewer, { [styles.inline]: inline })}></div>}>
            <VideoViewer
              id={`asset_viewer_${widgetId ? widgetId : id}`}
              className={cx(styles.videoViewer, { [styles.inline]: inline })}
              sources={sources}
              src={sourceUrl}
              metadata={metadata}
              loadingComponent={() => {
                return (
                  <div className={cx(styles.loader, {[isDarkMode? styles.darkLoader : styles.lightLoader]: lightBox})}>
                    <div className={styles.videoWrapper}>
                      {!lightBox && <video loop autoPlay playsInline muted>
                        <source src="/images/Workspaceloader.webm" type="video/webm" />
                        <source src="/images/Workspaceloader.mp4" type="video/mp4" />
                      </video>}
                      <label className={cx(styles.message, {[isDarkMode? styles.darkMessage : styles.lightMessage]: lightBox})}>
                        Loading...
                      </label>
                    </div>
                  </div>
                );
              }}
              active={active}
              icon={iconUrl}
              imageCDN={context.imageCDN}
              poster={poster}
              onPlay={() => {
                // console.log("Video started playing");
              }}
              isDarkMode={isDarkMode}
              lightBox={lightBox}
              onAnalyticsChange={onAnalyticsChange}
              onDebugAnalytics={onDebugAnalytics}
            />
          </Suspense>
        );
      }
    }

    case 'audio_viewer': {
      const id = _.get(asset, 'content.asset.id');
      const metadata = _.get(asset, 'content.asset.metadata');
      const sourceUrl = _.get(asset, 'content.asset.viewer.url');
      const supportedFormats = ['mp3', 'wav', 'aiff', 'flac', 'wma', 'aac'];
      const icon = getContentForFieldSelector(asset, 'icon');
      const iconUrl = _.get(icon, 'url') || '/public/cleverstory/v2/static/default_card.jpg';
      return (
        <Suspense fallback={<div className={styles.defaultViewer}></div>}>
          { _.includes(supportedFormats, _.get(metadata, 'extension')) &&
            <AudioViewer
              id={`asset_viewer_${widgetId ? widgetId : id}`}
              className={cx(styles.videoViewer, { [styles.inline]: inline })}
              src={sourceUrl}
              metadata={metadata}
              active={active}
              icon={iconUrl}
              imageCDN={context.imageCDN}
              poster={poster}
              onPlay={() => {
                // console.log("Video started playing");
              }}
              isDarkMode={isDarkMode}
              lightBox={lightBox}
              onAnalyticsChange={onAnalyticsChange}
              onDebugAnalytics={onDebugAnalytics}
            />
          }
          {!_.includes(supportedFormats, _.get(metadata, 'extension')) &&
            <DefaultAssetViewer
              asset={asset}
              active={active}
              isDarkMode={isDarkMode}
              lightBox={lightBox}
              onAnalyticsChange={onAnalyticsChange}
              onDebugAnalytics={onDebugAnalytics}
            />
          }
        </Suspense>
      )
    }

    default:
      return (
        <Suspense fallback={<div className={styles.defaultViewer}></div>}>
          <DefaultAssetViewer
            asset={asset}
            active={active}
            isDarkMode={isDarkMode}
            lightBox={lightBox}
            onAnalyticsChange={onAnalyticsChange}
            onDebugAnalytics={onDebugAnalytics}
          />
        </Suspense>
      );
  }
}
